import { escapeRegExp } from 'lodash'

import sanitizer from '@app/adapters/sanitizer'
import { getFirmZeroFormat } from '@app/adapters/silverfin'
import type { CastToFloatOptions } from './types.ts'

export function capitalize(value: string): string {
  if (!value || !isString(value)) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export const castToFloat = (
  value: string | number | null | undefined
): number => {
  if (!value || Number.isNaN(value)) {
    return 0
  }

  if (typeof value === 'number') {
    return value
  }

  // Convert commas to dots, remove all dots except the last one
  return parseFloat(value.replace(/,/g, '.').replace(/\.(?=.*\.)/g, ''))
}

export const castToFloatLocalized = (
  value: string | number | null | undefined,
  { delimiter, separator }: CastToFloatOptions = {}
): number => {
  if (!value || Number.isNaN(value)) {
    return 0
  }

  if (typeof value === 'number') {
    return value
  }

  if (value.startsWith('(') && value.endsWith(')')) {
    value = '-' + value.slice(1, -1)
  }

  const delimiterRegex = new RegExp(
    escapeRegExp(
      delimiter || window.I18n.t('number.currency.format.delimiter')
    ),
    'g'
  )
  const separatorRegex = new RegExp(
    escapeRegExp(
      separator || window.I18n.t('number.currency.format.separator')
    ),
    'g'
  )

  return parseFloat(
    value.replace(delimiterRegex, '').replace(separatorRegex, '.')
  )
}

/**
 * Standardizes the different ways of representing 0 within Silverfin
 * to the character '0'. Depends on current firm settings for zero format
 * @param value - the value to parse
 * @param format - the desired zero format. Falls back to firm setting if not provided.
 * @returns '0' or the value unchanged
 */
export const parseSfZero = (value: string, format?: string): string => {
  const actualFormat =
    format || getFirmZeroFormat(window.Silverfin.currentFirmId)

  if (actualFormat === value) {
    return '0'
  }

  return value
}

function isString(value: string): boolean {
  return typeof value === 'string'
}

export const stringToQuote = (str: string): string => {
  if (!str || str.length === 0) return ''

  return (
    str
      .trim()
      .split('\n')
      .map((s) => `> ${s}`)
      .join('\n') + '\n\n'
  )
}

export const arrayToSentence = (array: Array<string>): string => {
  if (array.length === 0) return ''
  if (array.length === 1) return array[0]

  return (
    array.slice(0, -1).join(', ') +
    window.I18n.t('support.array.last_word_connector') +
    array[array.length - 1]
  )
}

export function decodeHtmlChars(html: string): string {
  const tempDivElement: HTMLElement = document.createElement('div')
  tempDivElement.innerHTML = sanitizer(html) as string

  return tempDivElement.textContent || tempDivElement.innerText || ''
}
